import { Box, Button, IconButton, Link, Stack, Typography } from '@mui/material';
import { CgCustomDomainRoutingRecord } from '@provision/landing-page-utils';
import {
  ChevronDown,
  ChevronRight,
  CircleCheck,
  CircleX,
  Clock,
  ExternalLink,
  Globe,
  Info,
  LifeBuoy,
  LoaderCircle,
  RefreshCw,
  Trash2,
} from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { Tooltip } from 'src/components';
import { IconTile } from 'src/components/IconTile';
import { type SelectMenuOption } from 'src/components/SelectMenu';
import {
  SETTINGS_SECTION_ROW_PADDING,
  SettingsSectionCard,
} from 'src/components/SettingsSection';
import { type CgCustomDomain } from 'src/graphql/entities/cg-custom-domain/cg-custom-domain.types';
import { Modals, useShowModals } from 'src/store/modals';
import { getHumanReadableDate } from 'src/utils/date';
import {
  CgCustomDomainDisplayState,
  getCgCustomDomainDisplayState,
  isRoutingRecordFound,
  splitHostname,
} from './cg-custom-domain-display';
import { CgCustomDomainRecord } from './cg-custom-domain-record';
import { CgCustomDomainSegments } from './cg-custom-domain-segments';
import { foldButtonSx, inlineIconSx, mutedSx } from './styles';

const STATUS_LINES: Record<
  CgCustomDomainDisplayState,
  { label: string; icon: ReactNode; color: string }
> = {
  [CgCustomDomainDisplayState.Waiting]: {
    label: 'Waiting on your DNS record',
    icon: <Clock />,
    color: 'warning.main',
  },
  [CgCustomDomainDisplayState.Finishing]: {
    label: 'Record found, finishing setup',
    icon: <LoaderCircle />,
    color: 'warning.main',
  },
  [CgCustomDomainDisplayState.Live]: {
    label: 'Live',
    icon: <CircleCheck />,
    color: 'success.main',
  },
  [CgCustomDomainDisplayState.Stopped]: {
    label: 'Setup stopped',
    icon: <CircleX />,
    color: 'error.main',
  },
};

const explanation = (state: CgCustomDomainDisplayState, apex: string): ReactNode => {
  switch (state) {
    case CgCustomDomainDisplayState.Waiting:
      return (
        <>
          Add the record below where you manage DNS for <b>{apex}</b>. Once we find it, we
          finish the rest. We check every few minutes, or you can check now.
        </>
      );
    case CgCustomDomainDisplayState.Finishing:
      return 'We found your record and are securing the address. This usually takes a few minutes, and there is nothing else to do.';
    case CgCustomDomainDisplayState.Live:
      return 'Clients reach their membership pages and sign in at this address, and the links we send them use it.';
    case CgCustomDomainDisplayState.Stopped:
      return 'We couldn’t finish setting up this address, and it needs someone on our team. Your current address keeps working in the meantime.';
  }
};

interface CgCustomDomainCardProps {
  domain: CgCustomDomain;
  segmentOptions: SelectMenuOption[];
  onSegmentsChange: (brandIds: string[]) => void;
  isChecking: boolean;
  onCheckNow: () => void;
  onRemove: () => void;
}

export const CgCustomDomainCard = ({
  domain,
  segmentOptions,
  onSegmentsChange,
  isChecking,
  onCheckNow,
  onRemove,
}: CgCustomDomainCardProps) => {
  const [showRecords, setShowRecords] = useState(false);
  const [showContactSupport] = useShowModals(Modals.ContactSupportModal);

  const state = getCgCustomDomainDisplayState(domain);
  const status = STATUS_LINES[state];
  const { apex } = splitHostname(domain.hostname);

  const isLive = state === CgCustomDomainDisplayState.Live;
  const canCheck =
    state === CgCustomDomainDisplayState.Waiting ||
    state === CgCustomDomainDisplayState.Finishing;
  const pointsElsewhere = domain.routingRecord === CgCustomDomainRoutingRecord.WrongValue;

  const record = (
    <CgCustomDomainRecord
      hostname={domain.hostname}
      cnameTarget={domain.cnameTarget}
      isFound={isRoutingRecordFound(domain)}
    />
  );

  return (
    <SettingsSectionCard>
      <Box sx={SETTINGS_SECTION_ROW_PADDING}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <IconTile>
            <Globe />
          </IconTile>

          <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
            {isLive ? (
              <Link
                href={`https://${domain.hostname}`}
                target="_blank"
                rel="noopener"
                variant="subtitle1"
                fontWeight={600}
                sx={inlineIconSx}
              >
                {domain.hostname}
                <ExternalLink />
              </Link>
            ) : (
              <Typography variant="subtitle1" fontWeight={600}>
                {domain.hostname}
              </Typography>
            )}

            <Typography variant="caption" sx={mutedSx}>
              {domain.lastCheckedAt
                ? `Checked ${getHumanReadableDate(domain.lastCheckedAt)}`
                : 'Not checked yet'}
            </Typography>

            <Typography
              variant="body2"
              fontWeight={600}
              sx={{ ...inlineIconSx, color: status.color }}
            >
              {status.icon}
              {status.label}
            </Typography>

            <Typography variant="body2" sx={mutedSx}>
              {explanation(state, apex)}
            </Typography>
          </Stack>

          <Stack direction="row" spacing={0.5} alignItems="center">
            {canCheck ? (
              <Button
                variant="outlined"
                size="small"
                onClick={onCheckNow}
                disabled={isChecking}
                startIcon={<RefreshCw size={16} />}
              >
                {isChecking ? 'Checking' : 'Check now'}
              </Button>
            ) : null}
            <Tooltip title={`Remove ${domain.hostname}`}>
              <IconButton
                size="small"
                onClick={onRemove}
                aria-label={`Remove ${domain.hostname}`}
              >
                <Trash2 size={18} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Box>

      <CgCustomDomainSegments
        brandIds={domain.brandIds}
        isLive={isLive}
        options={segmentOptions}
        onChange={onSegmentsChange}
      />

      {pointsElsewhere ? (
        <Box sx={SETTINGS_SECTION_ROW_PADDING}>
          <Typography variant="body2" sx={{ ...inlineIconSx, alignItems: 'flex-start' }}>
            <Info />
            {domain.hostname} points to another site today. That site keeps working until you
            change the record; then the address is unavailable for a few minutes while we
            secure it.
          </Typography>
        </Box>
      ) : null}

      {state === CgCustomDomainDisplayState.Stopped ? (
        <Box sx={SETTINGS_SECTION_ROW_PADDING}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<LifeBuoy size={16} />}
            onClick={() => showContactSupport()}
          >
            Contact support
          </Button>
        </Box>
      ) : null}

      {isLive ? (
        <>
          <Box sx={SETTINGS_SECTION_ROW_PADDING}>
            <Button
              sx={foldButtonSx}
              onClick={() => setShowRecords((open) => !open)}
              aria-expanded={showRecords}
              startIcon={showRecords ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            >
              <Box component="span" sx={{ flex: 1, textAlign: 'left' }}>
                DNS records
              </Box>
              <Typography component="span" variant="caption" sx={{ color: 'success.main' }}>
                1 found
              </Typography>
            </Button>
          </Box>
          {showRecords ? (
            <Box sx={SETTINGS_SECTION_ROW_PADDING}>
              <Typography variant="body2" sx={{ ...mutedSx, mb: 1.5 }}>
                Leave this in place. Removing it takes the address offline.
              </Typography>
              {record}
            </Box>
          ) : null}
        </>
      ) : (
        <Box sx={SETTINGS_SECTION_ROW_PADDING}>
          <Typography variant="body2" sx={{ ...mutedSx, mb: 1.5 }}>
            Add this record. In the name, most DNS providers want only the part before {apex},
            shown in bold. If yours asks for the full name, include it.
          </Typography>
          {record}
        </Box>
      )}
    </SettingsSectionCard>
  );
};
